/**
 * Lightweight quote markup (decision 107, 2026-08-08) — the cover note and
 * footer take BASIC formatting: **bold**, _italic_, __underline__ and "- "
 * bullet lines, plus paragraph breaks (blank line) and line breaks (newline).
 *
 * Deliberately NOT markdown and NOT HTML: the text stays human-readable in the
 * textarea (snippets, tokens and "save as snippet" all keep working on plain
 * strings), and nothing that reaches the database can smuggle markup the
 * renderer didn't define. Unknown/unclosed markers stay visibly literal — the
 * same philosophy as unresolved snippet tokens (#106).
 *
 * One parser feeds every surface: the React renderer (builder preview +
 * customer page + the print/PDF render, which is the customer page) and the
 * HTML-string renderer (the send email). Change the grammar here only.
 */

/** A run of text with its inline styles. */
export interface MarkupRun {
  text: string
  bold?: boolean
  italic?: boolean
  underline?: boolean
}

/** A block: paragraph (lines joined by <br>) or bullet list. */
export type MarkupBlock =
  | { kind: 'p'; lines: MarkupRun[][] }
  | { kind: 'ul'; items: MarkupRun[][] }

interface Marker { token: string; style: 'bold' | 'italic' | 'underline' }

// Longest token first — '__' must win over '_' at the same position.
const MARKERS: Marker[] = [
  { token: '**', style: 'bold' },
  { token: '__', style: 'underline' },
  { token: '_', style: 'italic' },
]

interface Styles { bold?: boolean; italic?: boolean; underline?: boolean }

/** Parse one line's inline markers into styled runs. Recursive so **_both_**
 * nests; an unclosed marker is literal text (never swallowed). */
function parseInline(text: string, styles: Styles = {}): MarkupRun[] {
  const runs: MarkupRun[] = []
  let plain = ''
  const flush = () => {
    if (plain) { runs.push({ text: plain, ...styles }); plain = '' }
  }
  // Underscore markers only open/close at word boundaries, so snake_case_names
  // in prose never italicise (the intraword rule markdown uses for `_`).
  const wordChar = (c: string | undefined) => c != null && /[A-Za-z0-9]/.test(c)
  let i = 0
  while (i < text.length) {
    let matched = false
    for (const m of MARKERS) {
      if (styles[m.style]) continue                       // already inside this style
      if (!text.startsWith(m.token, i)) continue
      if (m.token.startsWith('_') && wordChar(text[i - 1])) continue
      let close = text.indexOf(m.token, i + m.token.length)
      if (m.token.startsWith('_')) {
        while (close !== -1 && wordChar(text[close + m.token.length])) {
          close = text.indexOf(m.token, close + 1)
        }
      }
      // A real span needs a closer and non-empty content.
      if (close > i + m.token.length - 1 && close > i) {
        const inner = text.slice(i + m.token.length, close)
        if (inner.trim() !== '') {
          flush()
          runs.push(...parseInline(inner, { ...styles, [m.style]: true }))
          i = close + m.token.length
          matched = true
          break
        }
      }
    }
    if (!matched) { plain += text[i]; i += 1 }
  }
  flush()
  return runs
}

const BULLET = /^\s*(?:-|•)\s+(.*)$/

/** Parse the whole text into blocks. Never throws; empty input → []. */
export function parseQuoteMarkup(text: string): MarkupBlock[] {
  const lines = String(text ?? '').replace(/\r\n?/g, '\n').split('\n')
  const blocks: MarkupBlock[] = []
  let para: MarkupRun[][] | null = null
  let list: MarkupRun[][] | null = null
  const closePara = () => { if (para && para.length) blocks.push({ kind: 'p', lines: para }); para = null }
  const closeList = () => { if (list && list.length) blocks.push({ kind: 'ul', items: list }); list = null }

  for (const raw of lines) {
    const bullet = BULLET.exec(raw)
    if (bullet) {
      closePara()
      if (!list) list = []
      list.push(parseInline(bullet[1]))
      continue
    }
    if (raw.trim() === '') { closePara(); closeList(); continue }
    closeList()
    if (!para) para = []
    para.push(parseInline(raw))
  }
  closePara()
  closeList()
  return blocks
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

function runHtml(r: MarkupRun): string {
  let h = escapeHtml(r.text)
  if (r.bold) h = `<strong>${h}</strong>`
  if (r.italic) h = `<em>${h}</em>`
  if (r.underline) h = `<u>${h}</u>`
  return h
}

const lineHtml = (runs: MarkupRun[]) => runs.map(runHtml).join('')

/**
 * Render to an HTML STRING — for the send email only (the app surfaces use the
 * React renderer). Everything is escaped before markup tags are added, so the
 * output is safe to inline into the mail body.
 */
export function renderMarkupHtml(text: string): string {
  return parseQuoteMarkup(text).map((b) =>
    b.kind === 'p'
      ? `<p style="margin:0 0 8px">${b.lines.map(lineHtml).join('<br/>')}</p>`
      : `<ul style="margin:0 0 8px;padding-left:20px">${b.items.map((i) => `<li>${lineHtml(i)}</li>`).join('')}</ul>`,
  ).join('')
}

// ---------------------------------------------------------------------------
// Toolbar text-surgery (pure — the React toolbar in src/lib/markup.tsx calls
// these; here so they unit-test beside the grammar they write).
// ---------------------------------------------------------------------------

export interface ToolbarResult { text: string; selStart: number; selEnd: number }

/** Wrap the selection in `marker` (or unwrap when already exactly wrapped). */
export function toggleWrap(text: string, selStart: number, selEnd: number, marker: string): ToolbarResult {
  const before = text.slice(0, selStart)
  const sel = text.slice(selStart, selEnd)
  const after = text.slice(selEnd)
  const m = marker.length
  // Unwrap when the selection itself, or its surroundings, carry the marker.
  if (sel.startsWith(marker) && sel.endsWith(marker) && sel.length >= m * 2) {
    const inner = sel.slice(m, sel.length - m)
    return { text: before + inner + after, selStart, selEnd: selStart + inner.length }
  }
  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      text: before.slice(0, -m) + sel + after.slice(m),
      selStart: selStart - m, selEnd: selEnd - m,
    }
  }
  const wrapped = marker + (sel || '') + marker
  return {
    text: before + wrapped + after,
    // Empty selection: park the caret between the markers, ready to type.
    selStart: selStart + m,
    selEnd: selStart + m + sel.length,
  }
}

/** Toggle "- " on every line the selection touches. */
export function toggleBullets(text: string, selStart: number, selEnd: number): ToolbarResult {
  const lineStart = text.lastIndexOf('\n', Math.max(0, selStart - 1)) + 1
  let lineEnd = text.indexOf('\n', Math.max(selEnd, selStart))
  if (lineEnd === -1) lineEnd = text.length
  const chunk = text.slice(lineStart, lineEnd)
  const lines = chunk.split('\n')
  const allBulleted = lines.every((l) => /^\s*(?:-|•)\s+/.test(l) || l.trim() === '')
  const next = lines.map((l) => {
    if (l.trim() === '') return l
    return allBulleted ? l.replace(/^(\s*)(?:-|•)\s+/, '$1') : (/^\s*(?:-|•)\s+/.test(l) ? l : `- ${l}`)
  }).join('\n')
  const delta = next.length - chunk.length
  return {
    text: text.slice(0, lineStart) + next + text.slice(lineEnd),
    selStart: lineStart,
    selEnd: lineEnd + delta,
  }
}

/** Does the text carry any markup the renderer would transform? (UI hint use.) */
export function hasMarkup(text: string): boolean {
  const blocks = parseQuoteMarkup(text)
  return blocks.some((b) =>
    b.kind === 'ul'
    || b.lines.some((l) => l.some((r) => r.bold || r.italic || r.underline)))
}
